import React, { useEffect, useState } from 'react';
import ArticleModal from './models/ArticleModal';
import { Article } from '../../types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { deleteArticle, fetchArticleDetailsAdmin, fetchArticles } from '../../features/admin/articleSlice';
import { FiEdit, FiTrash2 } from 'react-icons/fi';
import ConfirmDeleteModal from './models/ConfirmDeleteModal';
import InnerLoading from '../ui/InnerLoading';
import ScrollButton from '../ui/ScrollButton';


const ArticlePage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { articles, loading } = useSelector((state: RootState) => state.articleSlice);

    const [modalOpen, setModalOpen] = useState(false);
    const [editArticle, setEditArticle] = useState<Article | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);

    useEffect(() => {
        dispatch(fetchArticles());
    }, [dispatch]);

    const handleDelete = async () => {
        if (selectedArticleId !== null) {
            await dispatch(deleteArticle(selectedArticleId));
            dispatch(fetchArticles());
            setSelectedArticleId(null);
        }
    };

    const handleEdit = (articleData: Article) => {
        dispatch(fetchArticleDetailsAdmin(articleData.id))
            .unwrap()
            .then((data) => {
                setEditArticle(data);
                setModalOpen(true);
            })
            .catch(() => {
                console.error("Failed to fetch service data");
            });
    };

    const handleAdd = () => {
        setEditArticle(null);
        setModalOpen(true);
    };

    const confirmDelete = (id: number) => {
        setSelectedArticleId(id);
        setDeleteModalOpen(true);

    };

    return (
        <div >
            <div className='bg-gradient-to-r from-blue-600 to-indigo-500 text-white rounded-xl shadow-md p-6 mb-6'>
                <h2 className='text-white text-lg font-semibold'>{t("Article-title")}</h2>
            </div>
            {loading ? (
                <InnerLoading />
            ) : articles.length === 0 ? (
                <p className="text-center text-gray-500">{t("no-articles")}</p>
            ) : (
                <ul className='grid xs:grid-cols-1 gap-5'>
                    {articles.map((article: Article) => (
                        <li key={article.id} className="border bg-white rounded-xl flex flex-col justify-between p-4 shadow space-y-2">
                            <div>
                                <div className='flex justify-between w-full pb-3'>
                                    <p className="text-primary ">{article.country}</p>
                                    <p className="text-primary ">{new Date(article.createAt).toLocaleDateString()}</p>
                                </div>
                                
                                <h3 className="text-xl pb-3 ">{article.title}</h3>
                                <h5 className=' text-lg pb-3'>{article.subTitle}</h5>
                                <p className=' text-textSecond pb-5 line-clamp-5'>{article.details}</p>
                                {article.link && (
                                    <a href={article.link.startsWith('http') ? article.link : `https://${article.link}`} className="text-blue-500 underline">
                                        {t("view-link")}
                                    </a>
                                )}
                                <div className=' w-full h-28 mt-3 '>
                                    <img className=' h-full' src={article.image} loading="lazy" alt="Article-image" title={article.title} />
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 mt-3">
                                <button
                                    onClick={() => handleEdit(article)}
                                    className="p-2 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-full transition"
                                    title={t("edit")}
                                >
                                    <FiEdit size={18} />
                                </button>
                                <button
                                    onClick={() => article.id && confirmDelete(article.id)}
                                    className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-full transition"
                                    title={t("delete")}
                                >
                                    <FiTrash2 size={18} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            <ScrollButton />
            <div className="flex justify-end w-full pt-6">
                <button
                    onClick={handleAdd}
                    className="bg-primary px-6 py-2 rounded-full text-white hover:bg-darkColor transition">
                    + {t("add-new-Article")}
                </button>
            </div>

            {modalOpen && (
                <ArticleModal
                    onClose={() => setModalOpen(false)}
                    onSuccess={() => dispatch(fetchArticles())}
                    initialData={editArticle}
                />
            )}

            <ConfirmDeleteModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={handleDelete}
                messageKey="delete-Article-confirm"
            />
        </div>
    );
};
export default ArticlePage;
